/** Phase 3B-1 — Benchmark chart + AI vs Market card.
 *
 * Two-line chart (WingWise + benchmark). Rebased-to-$10k, absolute-$ and
 * percent-return view modes. Tooltip surfaces the metrics the user asked for.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Legend } from "recharts";
import { AlertTriangle, TrendingUp, TrendingDown, Info } from "lucide-react";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import { qk } from "@/lib/queryKeys";

const BENCHMARKS = [
  { key: "SPY", label: "SPY · S&P 500" },
  { key: "QQQ", label: "QQQ · Nasdaq 100" },
  { key: "IWM", label: "IWM · Russell 2000" },
  { key: "CASH", label: "Cash" },
  { key: "BUY_HOLD", label: "Buy & Hold Traded Symbols" },
  { key: "EQUAL_WEIGHT_AI", label: "Equal-Weight AI Recs" },
  { key: "EQUAL_WEIGHT_EXECUTED", label: "Equal-Weight Executed" },
  { key: "SMA_20_50", label: "20/50 SMA Crossover" },
  { key: "MOMENTUM_12_1", label: "12-1 Momentum" },
];

const STATUS_TONE = {
  available:         "pnl-up border-current",
  partial_data:      "text-signal-yellow border-current",
  insufficient_data: "text-muted-foreground border-border/50",
  unavailable:       "text-muted-foreground border-border/50",
  not_comparable:    "pnl-down border-current",
};

const STATUS_LABEL = {
  available:         "Available",
  partial_data:      "Partial Data",
  insufficient_data: "Insufficient Data",
  unavailable:       "Unavailable",
  not_comparable:    "Not Comparable",
};

function fmtPct(v, d = 2) { return v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(d)}%`; }
function fmtNum(v, d = 2) { return v == null ? "—" : v.toFixed(d); }
function toneOf(v) { return v == null || v === 0 ? "" : v > 0 ? "pnl-up" : "pnl-down"; }

function rebase(curve, mode, base = 10_000) {
  if (!curve?.length) return [];
  const start = curve[0].value;
  if (mode === "absolute") return curve;
  if (mode === "rebased") return curve.map((p) => ({ ...p, value: (p.value / start) * base }));
  if (mode === "percent") return curve.map((p) => ({ ...p, value: (p.value / start - 1) * 100 }));
  return curve;
}

function mergeSeries(tm, bm) {
  const map = new Map();
  tm.forEach((p) => map.set(p.date, { date: p.date, wingwise: p.value }));
  bm.forEach((p) => {
    const row = map.get(p.date) || { date: p.date };
    row.benchmark = p.value;
    map.set(p.date, row);
  });
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function AiVsMarketCard({ tmReturn, bmReturn, alpha, benchmark }) {
  const tone = toneOf(tmReturn);
  const alphaTone = toneOf(alpha);
  return (
    <div className="grid-cell !p-3" data-testid="ai-vs-market-card">
      <div className="label-cap">AI vs Market · {benchmark}</div>
      <div className="grid grid-cols-3 gap-3 mt-2">
        <div>
          <div className="text-[10px] mono uppercase text-muted-foreground">WingWise</div>
          <div className={`mono text-xl ${tone}`}>{fmtPct(tmReturn)}</div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase text-muted-foreground">{benchmark}</div>
          <div className={`mono text-xl ${toneOf(bmReturn)}`}>{fmtPct(bmReturn)}</div>
        </div>
        <div>
          <div className="text-[10px] mono uppercase text-muted-foreground">Alpha</div>
          <div className={`mono text-xl ${alphaTone}`}>
            {alpha == null ? "—" : `${alpha > 0 ? "+" : ""}${alpha.toFixed(2)}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricStrip({ p }) {
  const kv = [
    { k: "Excess", v: fmtPct(p.excess_return_pct), tone: toneOf(p.excess_return_pct), tt: "WingWise return minus benchmark return over the same period." },
    { k: "Alpha", v: p.alpha != null ? `${p.alpha > 0 ? "+" : ""}${p.alpha.toFixed(2)}` : "—", tone: toneOf(p.alpha), tt: "Annualised risk-adjusted return above the benchmark." },
    { k: "Beta", v: fmtNum(p.beta, 2), tt: "Sensitivity to benchmark moves. 1 = same, <1 = defensive." },
    { k: "Track. Err.", v: fmtNum(p.tracking_error, 2), tt: "Standard deviation of daily excess return, annualised." },
    { k: "Info Ratio", v: fmtNum(p.information_ratio, 2), tone: toneOf(p.information_ratio), tt: "Excess return per unit of tracking error." },
    { k: "Corr", v: fmtNum(p.correlation, 2), tt: "Correlation of daily returns with the benchmark." },
    { k: "Vol", v: fmtNum(p.volatility_pct, 2) + "%", tt: "Annualised daily-return standard deviation." },
    { k: "Max DD", v: fmtPct(p.max_drawdown_pct, 2), tone: "pnl-down", tt: "Largest peak-to-trough decline of the WingWise equity curve." },
    { k: "BM Max DD", v: fmtPct(p.benchmark_max_drawdown_pct, 2), tone: "pnl-down", tt: "Benchmark equivalent." },
    { k: "N obs", v: p.observation_count, tt: "Number of overlapping trading-day observations." },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-px bg-border/40" data-testid="benchmark-metric-strip">
      {kv.map((c) => (
        <div key={c.k} className="p-2 bg-card/40" title={c.tt}>
          <div className="label-cap flex items-center gap-1">{c.k}<Info size={8} className="opacity-60"/></div>
          <div className={`mono text-xs mt-0.5 ${c.tone || ""}`}>{c.v}</div>
        </div>
      ))}
    </div>
  );
}

function ChartTooltip({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null;
  const tm = payload.find((p) => p.dataKey === "wingwise")?.value;
  const bm = payload.find((p) => p.dataKey === "benchmark")?.value;
  const suffix = mode === "percent" ? "%" : "";
  return (
    <div className="bg-card border border-border/60 p-2 text-xs" data-testid="benchmark-chart-tooltip">
      <div className="mono text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 space-y-0.5">
        <div className="flex items-center gap-2"><span className="w-2 h-0.5 bg-[#0A84FF]"/>WingWise <span className="mono ml-auto">{tm?.toLocaleString(undefined, { maximumFractionDigits: 2 })}{suffix}</span></div>
        <div className="flex items-center gap-2"><span className="w-2 h-0.5 bg-signal-yellow"/>Benchmark <span className="mono ml-auto">{bm?.toLocaleString(undefined, { maximumFractionDigits: 2 })}{suffix}</span></div>
      </div>
    </div>
  );
}

export default function BenchmarkSection({ filterParams }) {
  const [benchmark, setBenchmark] = useState("AUTO");
  const [secondary, setSecondary] = useState("");
  const [viewMode, setViewMode] = useState("rebased");

  const params = useMemo(() => {
    const q = { benchmark };
    if (secondary) q.secondary = secondary;
    if (filterParams?.portfolio_key) q.portfolio_key = filterParams.portfolio_key;
    if (filterParams?.date_from) q.date_from = filterParams.date_from;
    if (filterParams?.date_to) q.date_to = filterParams.date_to;
    if (filterParams?.user_id) q.user_id = filterParams.user_id;
    return q;
  }, [benchmark, secondary, filterParams]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: qk(["performance", "benchmarks"], { filters: params }),
    queryFn: async () => (await api.get("/performance/benchmarks", { params })).data,
    staleTime: STALE.MEDIUM,
    gcTime: GC.MEDIUM,
  });

  const primary = data?.primary;
  const chartData = useMemo(() => {
    if (!primary) return [];
    const tm = rebase(primary.trademind_curve || [], viewMode);
    const bm = rebase(primary.benchmark_curve || [], viewMode);
    return mergeSeries(tm, bm);
  }, [primary, viewMode]);

  if (isLoading) {
    return (
      <section className="space-y-2" aria-labelledby="perf-benchmark-title">
        <h2 id="perf-benchmark-title" className="label-cap">Benchmark Comparison</h2>
        <div className="grid-cell text-xs text-muted-foreground">Loading benchmark…</div>
      </section>
    );
  }
  if (isError) {
    return (
      <div className="grid-cell border-signal-red/60 bg-signal-red/5 text-xs" role="alert">
        Failed to load benchmark data. <button onClick={() => refetch()} className="underline underline-offset-2 hover:text-foreground" data-testid="benchmark-retry">Retry</button>
      </div>
    );
  }
  if (!primary) return null;

  const status = primary.status || "unavailable";
  const canShowMetrics = ["available", "partial_data"].includes(status);

  return (
    <section className="space-y-3" aria-labelledby="perf-benchmark-title" data-testid="perf-benchmark-section">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 id="perf-benchmark-title" className="label-cap">AI vs Market</h2>
        <select value={benchmark} onChange={(e) => setBenchmark(e.target.value)}
          className="bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
          data-testid="benchmark-picker" aria-label="Benchmark">
          <option value="AUTO">Auto</option>
          {BENCHMARKS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <span className={`chip !text-[10px] uppercase ${STATUS_TONE[status] || ""}`} data-testid={`benchmark-status-${status}`}>
          {STATUS_LABEL[status] || status}
        </span>
        <div className="ml-auto flex items-center gap-1" role="tablist" aria-label="Chart view mode">
          {[
            { k: "rebased",  l: "Growth of $10k" },
            { k: "absolute", l: "Absolute $" },
            { k: "percent",  l: "% Return" },
          ].map((v) => (
            <button key={v.k} onClick={() => setViewMode(v.k)}
              role="tab" aria-selected={viewMode === v.k}
              className={`chip !text-[10px] uppercase border-current transition-colors focus:outline-none focus:ring-1 focus:ring-current ${viewMode === v.k ? "bg-secondary/50 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`benchmark-view-${v.k}`}
            >{v.l}</button>
          ))}
        </div>
      </div>

      {status === "not_comparable" && (
        <div className="grid-cell border-signal-red/40 bg-signal-red/5 flex items-start gap-2 text-xs" role="status" data-testid="benchmark-not-comparable">
          <AlertTriangle size={14} className="pnl-down shrink-0 mt-0.5"/>
          <div>
            <div className="mono uppercase pnl-down text-[10px]">Not comparable</div>
            <div className="text-muted-foreground mt-0.5">{primary.reason?.replace(/_/g, " ") || "The benchmark cannot be aligned with the selected WingWise period. Try a different benchmark or expand the date range."}</div>
          </div>
        </div>
      )}
      {status === "unavailable" && (
        <div className="grid-cell border-border/50 bg-secondary/10 flex items-start gap-2 text-xs" role="status" data-testid="benchmark-unavailable">
          <AlertTriangle size={14} className="text-muted-foreground shrink-0 mt-0.5"/>
          <div>
            <div className="mono uppercase text-muted-foreground text-[10px]">Unavailable</div>
            <div className="text-muted-foreground mt-0.5">
              {primary.reason
                ? primary.reason.replace(/_/g, " ")
                : "Benchmark price data is not available for the selected WingWise period. This can happen when the market-data provider has not yet published bars for your close dates, or when the benchmark itself is not applicable. Try a different benchmark or a wider date range."}
            </div>
          </div>
        </div>
      )}
      {status === "insufficient_data" && (
        <div className="grid-cell border-signal-yellow/50 bg-signal-yellow/5 flex items-center gap-2 text-xs" role="status" data-testid="benchmark-insufficient">
          <AlertTriangle size={14} className="text-signal-yellow shrink-0"/>
          <span>Only {primary.observation_count || 0} overlapping trading day{primary.observation_count === 1 ? "" : "s"}. Alpha, beta and information-ratio require at least 10 observations. Curves are shown for context but comparison metrics are omitted.</span>
        </div>
      )}

      {canShowMetrics && (
        <AiVsMarketCard
          tmReturn={primary.return_pct}
          bmReturn={primary.benchmark_return_pct}
          alpha={primary.alpha}
          benchmark={primary.benchmark}
        />
      )}

      {chartData.length > 1 && (
        <div className="grid-cell !p-3">
          <ResponsiveContainer width="100%" height={260} minWidth={0}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 3" stroke="rgba(148,163,184,0.15)" vertical={false}/>
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickMargin={4}/>
              <YAxis stroke="#64748b" fontSize={10} tickFormatter={(v) => viewMode === "percent" ? `${v.toFixed(1)}%` : v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v.toFixed(0)}`} width={60}/>
              <Tooltip content={<ChartTooltip mode={viewMode}/>}/>
              <Legend wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}/>
              <ReferenceLine y={viewMode === "percent" ? 0 : (viewMode === "rebased" ? 10_000 : chartData[0]?.wingwise || 0)} stroke="rgba(148,163,184,0.3)" strokeDasharray="2 3"/>
              <Line type="monotone" dataKey="wingwise" name="WingWise" stroke="#0A84FF" dot={false} strokeWidth={1.75} isAnimationActive={false}/>
              <Line type="monotone" dataKey="benchmark" name={`${primary.benchmark}`} stroke="#EAB308" dot={false} strokeWidth={1.75} isAnimationActive={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {canShowMetrics && <MetricStrip p={primary}/>}
    </section>
  );
}
